import { useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CarryDocumentStatusBadge } from '@/components/carry-documents/CarryDocumentStatusBadge';
import { CarryDocumentStatusSelect } from '@/components/carry-documents/CarryDocumentStatusSelect';
import { CarryDocumentTitleCell } from '@/components/carry-documents/CarryDocumentTitleCell';
import {
  ANY,
  formatDocumentDate,
  type StatusFilter,
} from '@/components/carry-documents/carryDocumentStatus';
import { useMe } from '@/portal/shared/hooks/useMe';
import { CarryDocumentDetailsDialog } from './CarryDocumentDetailsDialog';
import { OpenToSignCard } from '@/components/carry-documents/OpenToSignCard';
import {
  AWAITING_YOUR_SIGNATURE,
  filterAndSortDocuments,
  nextStatusSort,
  type StatusSort,
} from './carryDocumentListValues';
import { useDownloadCarryDocument } from '../hooks/useCarry';
import type {
  CarryDocument,
  CarryFundDocuments,
} from '../services/carryService';

const SORT_ICONS = { none: ArrowUpDown, asc: ArrowUp, desc: ArrowDown };

function DocumentTitle({
  fundId,
  document,
}: {
  fundId: string;
  document: CarryDocument;
}) {
  const downloadMutation = useDownloadCarryDocument();

  if (!document.has_file) {
    return <span className="font-medium">{document.title}</span>;
  }
  return (
    <button
      type="button"
      className="text-left font-medium text-primary hover:underline disabled:opacity-50"
      disabled={downloadMutation.isPending}
      onClick={() => downloadMutation.mutate({ fundId, document })}
    >
      {document.title}
    </button>
  );
}

function StatusHeader({
  sort,
  onSortChange,
}: {
  sort: StatusSort;
  onSortChange: (sort: StatusSort) => void;
}) {
  const Icon = SORT_ICONS[sort];
  return (
    <TableHead>
      <button
        type="button"
        className="inline-flex items-center gap-1 hover:text-foreground"
        onClick={() => onSortChange(nextStatusSort(sort))}
        aria-label="Sort by status"
      >
        Status
        <Icon className="h-3.5 w-3.5" />
      </button>
    </TableHead>
  );
}

export function CarryFundDocumentsList({ fund }: { fund: CarryFundDocuments }) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(ANY);
  const [statusSort, setStatusSort] = useState<StatusSort>('none');
  const [detailsFor, setDetailsFor] = useState<CarryDocument | null>(null);
  const { data: me } = useMe();
  const holderName = me?.fund_member?.full_name ?? '-';
  const documents = filterAndSortDocuments(
    fund.documents,
    statusFilter,
    statusSort
  );

  return (
    <div className="space-y-6">
      <OpenToSignCard
        count={fund.documents.filter((d) => d.requires_my_signature).length}
        description={AWAITING_YOUR_SIGNATURE}
      />

      <CarryDocumentStatusSelect
        value={statusFilter}
        onChange={setStatusFilter}
      />

      {documents.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          {fund.documents.length === 0
            ? 'No documents for this fund yet.'
            : 'No documents match this status.'}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Document</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Holder</TableHead>
              <TableHead>Date</TableHead>
              <StatusHeader sort={statusSort} onSortChange={setStatusSort} />
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.map((document) => (
              <TableRow key={document.id}>
                <CarryDocumentTitleCell>
                  <DocumentTitle fundId={fund.fund_id} document={document} />
                </CarryDocumentTitleCell>
                <TableCell>{document.document_type.name}</TableCell>
                <TableCell>{holderName}</TableCell>
                <TableCell>
                  {formatDocumentDate(document.document_date)}
                </TableCell>
                <TableCell>
                  <CarryDocumentStatusBadge status={document.status} />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`View details of ${document.title}`}
                      title="View details"
                      onClick={() => setDetailsFor(document)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {detailsFor && (
        <CarryDocumentDetailsDialog
          document={detailsFor}
          fundName={fund.fund_name}
          holderName={holderName}
          onClose={() => setDetailsFor(null)}
        />
      )}
    </div>
  );
}
